import { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { Alert, Spinner } from "react-bootstrap";
import {
  useDeleteCategoryMutation,
  useGetAllCategories,
} from "../../../../../api/jeep-rally/categories/categories.hooks";
import DeleteModal from "../../../../components/DeleteModal";
import {
  filterCategories,
  getCategoryApiErrorMessage,
  isJeepRallyAdmin,
  normalizeCategories,
} from "../utils/category.utils";
import CategoriesHeader from "./_components/CategoriesHeader";
import CategoriesTable from "./_components/CategoriesTable";

const CategoriesListPage = () => {
  const history = useHistory();
  const canManage = isJeepRallyAdmin();
  const { data, isLoading, isError, error } = useGetAllCategories();
  const {
    mutate: deleteCategory,
    isLoading: isDeleting,
    isPending: isDeletePending,
  } = useDeleteCategoryMutation();

  const [searchTerm, setSearchTerm] = useState("");
  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const categories = useMemo(
    () => normalizeCategories(data),
    [data]
  );

  const filteredCategories = useMemo(() => {
    return filterCategories(categories, searchTerm);
  }, [categories, searchTerm]);

  const deleting = isDeleting || isDeletePending;

  const handleEdit = (category) => {
    const categoryId = category?._id || category?.id;
    if (!categoryId) return;
    history.push(`/jeep-rally/categories/edit/${categoryId}`, { category });
  };

  const openDeleteModal = (category) => {
    setSelectedCategory(category);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setShowDeleteModal(false);
    setSelectedCategory(null);
  };

  const handleConfirmDelete = () => {
    const categoryId = selectedCategory?._id || selectedCategory?.id;
    if (!categoryId || !canManage) return;

    setFeedback({ type: "", message: "" });
    deleteCategory(categoryId, {
      onSuccess: () => {
        setFeedback({
          type: "success",
          message: "Category deleted successfully.",
        });
        setShowDeleteModal(false);
        setSelectedCategory(null);
      },
      onError: (deleteError) => {
        setFeedback({
          type: "danger",
          message: getCategoryApiErrorMessage(
            deleteError,
            "Failed to delete category."
          ),
        });
      },
    });
  };

  if (isLoading) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body text-danger py-4">
            Failed to load categories.{" "}
            {getCategoryApiErrorMessage(error, "Please try again.")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="card">
        <CategoriesHeader
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          total={filteredCategories.length}
          canManage={canManage}
          onAddCategory={() => history.push("/jeep-rally/categories/create")}
        />
        <div className="card-body">
          {feedback.message && (
            <Alert variant={feedback.type} className="mb-3">
              {feedback.message}
            </Alert>
          )}

          {filteredCategories.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted mb-0">No categories found</p>
            </div>
          ) : (
            <CategoriesTable
              categories={filteredCategories}
              onEdit={handleEdit}
              onDelete={openDeleteModal}
              isDeleting={deleting}
              canManage={canManage}
            />
          )}
        </div>
      </div>

      <DeleteModal
        show={showDeleteModal}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        title="Delete Category"
        message={`Are you sure you want to delete ${
          selectedCategory?.title || "this category"
        }? Related rallies or teams may still reference the key "${
          selectedCategory?.key || ""
        }". This cannot be undone.`}
        confirmText="Delete Category"
        isLoading={deleting}
      />
    </div>
  );
};

export default CategoriesListPage;
